import { Component, EventEmitter, Input, Output } from "@angular/core";
import { NavController, NavParams, ViewController } from "ionic-angular";
import { Order, OrderComponent } from "models/order-management/models";
import { OrderFormState } from "pages/orders-entry-om/add-order/order-form-state";
import { StateManagementService } from "../../../providers/state-management/state-management.service";
import {
  OrderComponentInterface,
  OrderComponentPropertyUpdate,
  OrderCreateRequestUpdate,
} from "../order-components-interface";

@Component({
  selector: "text-field",
  templateUrl: "text-field.html",
})
export class OrderTextFieldComponent implements OrderComponentInterface {
  /****************************************************************************
   * Inputs                                                                   *
   ****************************************************************************/
  @Input() orderComponent: OrderComponent;
  @Input() orderCreateRequest: Order;
  @Input() get orderFormState(): OrderFormState {
    return this._orderFormState;
  }

  /****************************************************************************
   * Outputs                                                                  *
   ****************************************************************************/
  @Output() orderCreateRequestEvent =
    new EventEmitter<OrderCreateRequestUpdate>();
  @Output() orderFormStateEvent = new EventEmitter<OrderFormState>();
  @Output() isValidEvent = new EventEmitter<boolean>();
  @Output() amendedOrderRequestEvent =
    new EventEmitter<OrderComponentPropertyUpdate>();

  /****************************************************************************
   * State                                                                    *
   ****************************************************************************/
  textFieldValue: string = "";

  constructor(
    public navCtrl: NavController,
    public navParams: NavParams,
    public viewCtrl: ViewController,
    public stateData: StateManagementService
  ) {}

  private _orderFormState: OrderFormState = {};
  private isHidden = false;

  /****************************************************************************
   * Lifecycle Methods                                                        *
   ****************************************************************************/
  ngOnInit() {
    if (
      this.orderFormState.formType === "amendment" ||
      this.orderFormState.formType === "convertToOrder"
    ) {
      this.setAmendedValue();
    }
    this.checkIsValid();
  }

  /****************************************************************************
   * Getter Methods                                                           *
   ****************************************************************************/
  get isDisabled() {
    if (
      !this.orderComponent.properties[0].amendable &&
      this._orderFormState.formType === "amendment"
    ) {
      return true;
    }
    return false;
  }

  /****************************************************************************
   * Setter Methods                                                           *
   ****************************************************************************/
  set orderFormState(orderFormState: OrderFormState) {
    const componentField = this.orderComponent.properties[0].name;
    const shouldClear = OrderFormState.shouldClear(
      componentField,
      this._orderFormState,
      orderFormState
    );
    this._orderFormState = orderFormState;

    this.isHidden =
      componentField === "PRNReasons" && !this._orderFormState.PRN;

    if (shouldClear) {
      const updateObject: OrderCreateRequestUpdate = { updates: {} };
      updateObject.deletes = [componentField as keyof Order];
      this.orderCreateRequestEvent.emit(updateObject);
      this.textFieldValue = "";
    }
    this.checkIsValid();
  }

  setAmendedValue() {
    const componentField: string = this.orderComponent.properties[0].name;
    this.textFieldValue = this.orderCreateRequest[componentField] || "";
    this.checkIsValid();
  }

  /****************************************************************************
   * Component Related Methods                                                *
   ****************************************************************************/
  onTextFieldValueChange(event: string) {
    let value = "";
    if (event) {
      value = event;
    }
    if (
      this.orderComponent.properties[0].amendable &&
      this._orderFormState.formType === "amendment"
    ) {
      const componentField = this.orderComponent.properties[0].name;
      const prevValue = this.orderCreateRequest[componentField].toString();
      const amendedObject: OrderComponentPropertyUpdate = { updates: {} };
      if (prevValue !== value) {
        amendedObject.updates = {
          propertyName: componentField,
          previousValueDisplay: this.orderCreateRequest[componentField],
          previousValue: this.orderCreateRequest[componentField],
          newValue: value,
        };
        this.amendedOrderRequestEvent.emit(amendedObject);
      } else {
        const amendedObject: OrderComponentPropertyUpdate = {
          updates: {},
          deletes: [componentField as keyof Order],
        };
        this.amendedOrderRequestEvent.emit(amendedObject);
      }
    } else {
      const updateObject: OrderCreateRequestUpdate = { updates: {} };
      const componentField = this.orderComponent.properties[0].name;
      updateObject.updates[componentField] = value;
      this.orderCreateRequestEvent.emit(updateObject);
    }

    this.checkIsValid();
  }

  checkIsValid() {
    if (this.isRequired() && this.textFieldValue.length === 0) {
      this.isValidEvent.emit(false);
    } else {
      this.isValidEvent.emit(true);
    }
  }

  isRequired() {
    const componentField = this.orderComponent.properties[0].name;
    if (this.isHidden) {
      return false;
    } else if (componentField === "PRNReasons" && this.orderFormState.PRN) {
      return true;
    } else {
      return this.orderComponent.properties[0].required;
    }
  }

  /****************************************************************************
   * Cypress-Related ("data-cy") Methods                                      *
   ****************************************************************************/
  toKebabCase(label: string): string {
    if (label) {
      return label
        .replace(/^\s+|\s+$/gm, "")
        .replace(/([a-z])([A-Z])/g, "$1-$2")
        .replace(/[\s_]+/g, "-")
        .toLowerCase();
    }
    return "";
  }

  createDataCyAttribute(component: string, option: string): string {
    return `${this.toKebabCase(component)}-option-${this.toKebabCase(option)}`;
  }
}



// WEBPACK FOOTER //
// ./src/components/order-management/text-field/text-field.ts